import * as React from "react"
import {
  type WorkItem,
  WorkItemSupportType,
  type WorkItemTransaction,
  WorkItemType,
} from "@digits-graphql/frontend/graphql-bearer"

export const CATEGORY_WORK_ITEM_TYPES = [
  WorkItemType.HighConfidenceCategory,
  WorkItemType.UnassignedCategory,
  WorkItemType.InParentCategory,
]

export const VENDOR_WORK_ITEM_TYPES = [
  WorkItemType.HighConfidenceVendor,
  WorkItemType.UnassignedVendor,
]

export interface SupportingTransactions {
  object?: WorkItemTransaction
  from?: WorkItemTransaction
  to?: WorkItemTransaction
  userProvided?: WorkItemTransaction
  responseBased?: WorkItemTransaction
  objectHighlight?: WorkItemTransaction
  context: WorkItemTransaction[]
}

export function getSupportingTransactions(workItem: WorkItem): SupportingTransactions {
  const { transactions } = workItem
  const toWorkItemTransaction = transactions.find((t) => t.supportType === WorkItemSupportType.To)

  return {
    object: transactions.find((t) => t.supportType === WorkItemSupportType.Object),
    from: transactions.find((t) => t.supportType === WorkItemSupportType.From),
    to: toWorkItemTransaction,
    userProvided: transactions.find((t) => t.supportType === WorkItemSupportType.UserProvided),
    objectHighlight: transactions.find(
      (t) => t.supportType === WorkItemSupportType.ObjectHighlight
    ),
    responseBased: transactions.find(
      (t) => t.supportType === WorkItemSupportType.ResponseBasedPrediction
    ),
    context: transactions.filter((t) => t.supportType === WorkItemSupportType.Context),
  }
}

export function useSupportingTransactions(workItem: WorkItem): SupportingTransactions {
  // need to add transactions to the dependency list because it gets mutated in the cache
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => getSupportingTransactions(workItem), [workItem, workItem.transactions])
}

export function useOptionalSupportingTransactions(
  workItem?: WorkItem
): SupportingTransactions | Partial<SupportingTransactions> {
  // need to add transactions to the dependency list because it gets mutated in the cache
  return React.useMemo(
    () => (workItem ? getSupportingTransactions(workItem) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workItem, workItem?.transactions]
  )
}
